import { Component, EventEmitter, Output, OnInit, inject } from '@angular/core';
import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { ParentContextBannerComponent } from '../parent-context-banner/parent-context-banner.component';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { BillAiFlowStore } from '../state/bill-ai-flow.store';
import { BillImportService } from '../../../services/bill-import.service';
import { BillImportRequest, BillImportResponse } from '../../../models/bill-import.models';
import { BillNavEntry, BillAiFlowStep } from '../state/bill-ai-flow.models';
import { BillFile } from '../../../models';
import { BillService } from '../../../services/bill.service';
import { formatBillDate, formatBillPeriod } from '../../../utils/date-format.util';
import { cleanIdentifierName } from '../../../utils/bill-utils';
import { replacedBillsText } from '../state/duplicate-resolution.util';
import { validateLenStep, LenAddress } from '../bill-ai-flow-len-step/bill-ai-flow-len-step.validation';
import { validateSaStep } from '../bill-ai-flow-sa-step/bill-ai-flow-sa-step.validation';
import { validateMeterStep } from '../bill-ai-flow-meter-step/bill-ai-flow-meter-step.validation';
import { validateBillReviewStep } from '../bill-ai-flow-bill-review-step/bill-ai-flow-bill-review-step.validation';
import { groupErrorsBySummary, SummaryGroup } from '../validation';
import { FriendlyImportError, translateImportErrors } from './translate-import-error';

interface SummaryMeter {
  groupId: string;
  uan: string;
  utilityName: string;
  isNew: boolean;
  bills: SummaryBill[];
  /** Physical documents on this meter (a paired bill counts as 2) */
  billDocCount: number;
  totalAmount: number;
  totalUsage: number;
  energyPlanLabel: string | null;
  expanded: boolean;
}

/** One exact duplicate in the import summary: skipped, or replacing an existing bill. */
interface SummaryDuplicate {
  billGroupId: string;
  /** "Meter {number}, {dates} · replaces #{id}", "... · skipped" or "... · adds supply to #{id}" */
  text: string;
  actionLabel: 'Change' | 'Review';
}

interface SummaryBillChild {
  fileId: string;
  kind: string;
  label: string;
  file: BillFile;
}

interface SummarySa {
  groupId: string;
  address: string;
  name: string | null;
  isNew: boolean;
  meters: SummaryMeter[];
}

interface SummaryLen {
  groupId: string;
  name: string;
  isNew: boolean;
  serviceAddresses: SummarySa[];
}

@Component({
  selector: 'app-bill-ai-flow-summary-step',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, DecimalPipe, ParentContextBannerComponent],
  templateUrl: './bill-ai-flow-summary-step.component.html',
  styleUrl: './bill-ai-flow-summary-step.component.scss'
})
export class BillAiFlowSummaryStepComponent implements OnInit {
  @Output() importComplete = new EventEmitter<BillImportResponse>();

  summaryData: SummaryLen[] = [];
  importRequest: BillImportRequest | null = null;

  isImporting = false;
  importErrors: string[] = [];
  /** Friendly, actionable explanations of import errors (shown above the raw list) */
  importErrorGuidance: FriendlyImportError[] = [];
  importSuccess = false;
  importResponse: BillImportResponse | null = null;
  validationErrors: string[] = [];
  /** Grouped validation errors from all step validators (shown in banner) */
  validationGroups: SummaryGroup[] = [];
  hasValidationBlockers = false;

  // Exact duplicates (bills left out of the request are not in totalBills)
  replacingCount = 0;
  skippedCount = 0;
  /** Missing halves of existing dual bills, merged into them on import */
  mergingCount = 0;
  duplicateRows: SummaryDuplicate[] = [];

  // Totals
  totalBills = 0;
  totalAmount = 0;
  totalUsage = 0;
  newEntities = { lens: 0, sas: 0, meters: 0 };
  existingEntities = { lens: 0, sas: 0, meters: 0 };

  // Which stats/tree rows to show based on onboarding level
  showLenStat = true;
  showSaStat = true;
  showMeterStat = true;
  showLenInTree = true;
  showSaInTree = true;

  // Fast track
  fastTrackBannerCollapsed = true;
  fastTrackSkippedSteps: { key: string; label: string; reason?: string }[] = [];
  fastTrackWarningsList: string[] = [];

  /** Post-import line for bills that replaced existing ones; empty when none did. */
  replacedText = '';

  // Post-import navigation (pre-computed to avoid getters in template)
  goBackLabel = 'Back to Portfolio';
  showViewCustomer = false;

  // Bill preview
  activeBillGroupId: string | null = null;
  activeChildFileId: string | null = null;

  readonly state = inject(BillAiFlowStore);
  private billImportService = inject(BillImportService);
  private billService = inject(BillService);
  private router = inject(Router);

  ngOnInit(): void {
    this.state.setImportSuccess(false);
    this.buildSummary();
    this.buildFastTrackInfo();
  }

  goBack(): void {
    this.state.goBack();
  }

  /** Jump to the bill's review form to flip its Skip / Replace choice. */
  reviewDuplicate(row: SummaryDuplicate): void {
    this.state.goToBillReview(row.billGroupId);
  }

  toggleMeter(meter: SummaryMeter): void {
    meter.expanded = !meter.expanded;
  }

  previewBill(bill: SummaryBill): void {
    const billFile = this.state.getBillFileForGroup(bill.groupId);
    this.state.setPreviewBillFile(billFile);
    this.activeBillGroupId = bill.groupId;
    this.activeChildFileId = null;
  }

  toggleBillChildren(bill: SummaryBill): void {
    bill.expanded = !bill.expanded;
  }

  previewChild(bill: SummaryBill, child: SummaryBillChild): void {
    this.state.setPreviewBillFile(child.file);
    this.activeBillGroupId = bill.groupId;
    this.activeChildFileId = child.fileId;
  }

  runImport(): void {
    if (this.isImporting || !this.importRequest) return;

    this.isImporting = true;
    this.importErrors = [];
    this.importErrorGuidance = [];

    this.billImportService.import(this.importRequest)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.isImporting = false;
          this.importResponse = response;

          if (response.success) {
            this.importSuccess = true;
            this.replacedText = replacedBillsText(response.summary?.replacedBillCount ?? this.replacingCount);
            this.state.setImportSuccess(true);
            this.state.setPreviewBillFile(null);
            // Clear persisted session immediately so refresh/close doesn't leave stale data
            this.state.clearPersistedState();
            this.computePostImportNav();
          } else {
            const errorMessages = (response.errors || []).map(e => e.message).filter(Boolean);
            this.importErrors = errorMessages.length > 0 ? errorMessages : ['Import completed with errors'];
            this.importErrorGuidance = translateImportErrors(this.importErrors);
          }
        },
        error: (err) => {
          this.isImporting = false;
          this.importErrors = this.extractErrors(err);
        }
      });
  }

  private extractErrors(err: any): string[] {
    console.error('Bill import failed:', err);
    const body = err?.error;
    let rawMessages: string[];

    // Response body has an errors array (BillImportResponse shape)
    if (Array.isArray(body?.errors) && body.errors.length) {
      rawMessages = body.errors.map((e: any) => e.message || e.code || 'Unknown error');
    } else if (body?.message) {
      // Response body has a single message field
      rawMessages = [body.message];
    } else if (typeof body === 'string' && body.trim()) {
      // Response body is a string
      rawMessages = [body];
    } else if (err?.status === 0) {
      // Network/timeout errors
      rawMessages = ['Network error. Please check your connection and try again.'];
    } else {
      rawMessages = ['Import failed. Please try again.'];
    }

    this.importErrorGuidance = translateImportErrors(rawMessages);
    return rawMessages;
  }

  /** Navigate back to the step the friendly error is pointing at */
  goToStep(step: BillAiFlowStep): void {
    this.state.goToStep(step);
  }

  private buildSummary(): void {
    try {
      this.importRequest = this.state.buildImportRequest();
    } catch (e: any) {
      console.error('Failed to build import request:', e);
      const message = e?.message || 'Failed to prepare import data. Please go back and verify your selections.';
      this.importErrors = [message];
      // Route through the friendly-error translator so users get the
      // "Go to Legal Entities" action for duplicate-LEN errors etc.
      this.importErrorGuidance = translateImportErrors(this.importErrors);
      return;
    }

    this.buildDuplicateSummary();

    const response = this.state.matchResponse();
    const selections = this.state.entitySelections();
    if (!response) return;

    // Determine which stats are relevant based on onboarding level
    const ctx = this.state.getContext();
    const level = ctx?.level || 'PORTFOLIO';
    // Hide stats and tree rows for entities that are already known parents
    this.showLenStat = level === 'PORTFOLIO' || level === 'CUSTOMER' || level === 'LEN';
    this.showSaStat = level === 'PORTFOLIO' || level === 'CUSTOMER' || level === 'LEN' || level === 'SERVICE_ADDRESS';
    this.showMeterStat = true;
    this.showLenInTree = this.showLenStat;
    this.showSaInTree = this.showLenStat; // SA is known parent at SERVICE_ADDRESS and METER levels

    const billNav = this.state.getBillNavigation();

    const pairByUtilityFileId = new Map<string, { utilityFile: BillFile; supplierFile: BillFile }>();
    for (const pair of this.billService.pairedGroups()) {
      if (pair.utilityBill && pair.supplierBill) {
        pairByUtilityFileId.set(pair.utilityBill.id, { utilityFile: pair.utilityBill, supplierFile: pair.supplierBill });
      }
    }

    this.summaryData = [];
    this.totalBills = 0;
    this.totalAmount = 0;
    this.totalUsage = 0;
    this.newEntities = { lens: 0, sas: 0, meters: 0 };
    this.existingEntities = { lens: 0, sas: 0, meters: 0 };

    for (const lenImport of this.importRequest.legalEntities) {
      const lenIsNew = !lenImport.id;
      if (lenIsNew) this.newEntities.lens++; else this.existingEntities.lens++;

      const sas: SummarySa[] = [];

      for (const saImport of lenImport.serviceAddresses) {
        const saIsNew = !saImport.id;
        if (saIsNew) this.newEntities.sas++; else this.existingEntities.sas++;

        const meters: SummaryMeter[] = [];

        for (const meterImport of saImport.meters) {
          const meterIsNew = !meterImport.id;
          if (meterIsNew) this.newEntities.meters++; else this.existingEntities.meters++;

          const meterBills: SummaryBill[] = [];
          let meterTotal = 0;
          let meterUsage = 0;

          for (const billImport of meterImport.bills) {
            const navEntry = billNav.find(n => n.billGroupId === billImport.groupId);
            const billFile = this.state.getBillFileForGroup(billImport.groupId);

            const billEpId = billImport.energyPlanId;
            const billEpLabel: string | null = billEpId ? `Plan #${billEpId}` : null;

            const pairInfo = billFile ? pairByUtilityFileId.get(billFile.id) : undefined;
            let children: SummaryBillChild[] | undefined;
            if (pairInfo) {
              children = [
                { fileId: pairInfo.utilityFile.id, kind: 'Utility', label: this.childLabel(pairInfo.utilityFile, pairInfo.utilityFile.data?.utilityName), file: pairInfo.utilityFile },
                { fileId: pairInfo.supplierFile.id, kind: 'Supplier', label: this.childLabel(pairInfo.supplierFile), file: pairInfo.supplierFile },
              ];
            }

            meterBills.push({
              groupId: billImport.groupId,
              startDate: billImport.billStartDate ? formatBillDate(billImport.billStartDate) : '',
              endDate: billImport.billEndDate ? formatBillDate(billImport.billEndDate) : '',
              amount: billImport.totalAmount,
              usage: billImport.totalUsage,
              provider: billImport.providerName || 'On Utility',
              isDual: billImport.billType === 'DUAL',
              fileName: billFile?.file?.name || '',
              energyPlanLabel: billEpLabel,
              children,
              expanded: false,
            });

            meterTotal += billImport.totalAmount || 0;
            meterUsage += billImport.totalUsage || 0;
            this.totalBills++;
          }

          this.totalAmount += meterTotal;
          this.totalUsage += meterUsage;

          const meterEpLabels = new Set(meterBills.map(b => b.energyPlanLabel).filter(Boolean));
          const meterEpLabel = meterEpLabels.size === 1
            ? Array.from(meterEpLabels)[0]!
            : meterEpLabels.size > 1
              ? `${meterEpLabels.size} plans`
              : null;

          meters.push({
            groupId: meterImport.groupId,
            uan: this.formatMeterUan(meterImport),
            utilityName: meterImport.utilityId ? '' : '',
            isNew: meterIsNew,
            bills: meterBills,
            billDocCount: meterBills.length + meterBills.filter(b => b.children).length,
            totalAmount: meterTotal,
            totalUsage: meterUsage,
            energyPlanLabel: meterEpLabel,
            expanded: false,
          });
        }

        sas.push({
          groupId: saImport.groupId,
          address: [saImport.street1, saImport.city, saImport.state].filter(Boolean).join(', '),
          name: this.state.saEdits()[saImport.groupId]?.label?.trim() || null,
          isNew: saIsNew,
          meters,
        });
      }

      this.summaryData.push({
        groupId: lenImport.groupId,
        name: lenImport.name || 'Unknown',
        isNew: lenIsNew,
        serviceAddresses: sas,
      });
    }

    // Validate required fields by running all four step validators
    this.validationErrors = [];
    this.runAggregatedValidation();

    // Check for cross-LEN duplicate service addresses (not covered by per-step validators)
    const crossLenDupes = this.state.crossLenDuplicateSas();
    for (const group of crossLenDupes) {
      const addresses = group.saGroupIds.map(saId => {
        for (const len of this.importRequest!.legalEntities) {
          const sa = len.serviceAddresses.find(s => s.groupId === saId);
          if (sa) return [sa.street1, sa.city, sa.state].filter(Boolean).join(', ');
        }
        return saId;
      });
      const unique = [...new Set(addresses)];
      this.validationErrors.push(
        `Duplicate address "${unique[0] || 'Unknown'}" found across ${group.lenGroupIds.length} legal entities — merge the addresses or entities in the Addresses step.`
      );
    }
  }

  private buildDuplicateSummary(): void {
    const rowText = (billGroupId: string, outcome: string) => {
      const data = this.state.billData()[billGroupId];
      const meter = this.state.getBillMeterLabels(billGroupId)?.meter ?? 'Unknown';
      return `Meter ${meter}, ${formatBillPeriod(data?.billingPeriodStart, data?.billingPeriodEnd)} · ${outcome}`;
    };
    const merging: SummaryDuplicate[] = this.state.billsWithDuplicates()
      .filter(d => d.duplicate.pairable && !this.state.isPairRejected(d.billGroupId))
      .map(({ billGroupId, duplicate }) => ({
        billGroupId,
        text: rowText(billGroupId, `adds ${duplicate.pairSide === 'SUPPLY' ? 'supply' : 'distribution'} to #${duplicate.existingBillId}`),
        actionLabel: 'Review' as const,
      }));
    const decided: SummaryDuplicate[] = this.state.duplicateDecisions().map(({ billGroupId, duplicate, action }) => {
      const replace = action === 'REPLACE';
      return {
        billGroupId,
        text: rowText(billGroupId, replace ? `replaces #${duplicate.existingBillId}` : 'skipped'),
        actionLabel: replace ? 'Change' as const : 'Review' as const,
      };
    });
    this.duplicateRows = [...merging, ...decided];
    this.mergingCount = merging.length;
    this.replacingCount = this.state.replacedBillGroupIds().size;
    this.skippedCount = this.state.skippedBillGroupIds().size;
  }

  /** Display label for a paired bill's source document */
  private childLabel(file: BillFile, preferredName?: string): string {
    const d = file.data;
    const dates = formatBillPeriod(d?.billingPeriodStart, d?.billingPeriodEnd);
    const name = preferredName || d?.providerName;
    return name ? `${name}: ${dates}` : dates || file.file?.name || '';
  }

  /**
   * Format a meter's UAN for display on the summary card. Prefers the
   * canonical utility_account_number label persisted on any of the meter's
   * bills, falls back to the AI-extracted verbatim name from
   * additionalIdentifiers, then to the raw value with no prefix. Returns
   * "Unknown" when no UAN was set.
   */
  private formatMeterUan(meterImport: { groupId?: string; utilityAccountNumber1?: string; bills?: Array<{ groupId?: string }> }): string {
    const value = meterImport.utilityAccountNumber1;
    if (!value) return 'Unknown';

    for (const bill of meterImport.bills || []) {
      if (!bill.groupId) continue;
      const data = this.state.getBillFileForGroup(bill.groupId)?.data;
      if (data?.utilityAccountNumberLabel) {
        return `${data.utilityAccountNumberLabel}: ${value}`;
      }
      if (data?.additionalIdentifiers) {
        const match = data.additionalIdentifiers.find(id => id.value === value);
        if (match?.name) {
          const cleaned = cleanIdentifierName(match.name);
          if (cleaned) return `${cleaned}: ${value}`;
        }
      }
    }
    return value;
  }

  /** Run all four step validators and aggregate the results into a single banner */
  private runAggregatedValidation(): void {
    const legalEntities = this.state.matchResponse()?.legalEntities ?? [];
    const isNewEntity = (gid: string) => this.state.isNewEntity(gid);

    const lenResult = validateLenStep({
      legalEntities,
      entityNameOverrides: this.state.entityNameOverrides(),
      extractedLenNames: this.buildExtractedLenNamesFromRequest(),
      lenAddressDataMap: this.buildLenAddressMapFromRequest(),
      isNewEntity,
      selections: this.state.entitySelections(),
    });

    const saResult = validateSaStep({
      legalEntities,
      saEdits: this.state.saEdits(),
      isNewEntity,
    });

    const meterResult = validateMeterStep({
      legalEntities,
      meterEdits: this.state.meterEdits(),
      isNewEntity,
    });

    const billResult = validateBillReviewStep({
      billData: this.state.billData(),
      billNavigation: this.state.billNavigation(),
      excludeBillGroupIds: this.state.skippedBillGroupIds(),
      isNewEntity,
    });

    const allErrors = [
      ...lenResult.errors,
      ...saResult.errors,
      ...meterResult.errors,
      ...billResult.errors,
    ];
    this.validationGroups = groupErrorsBySummary(allErrors);
    this.hasValidationBlockers = allErrors.length > 0;
  }

  /** Build a LenAddress map by projecting the match request's billing addresses */
  private buildLenAddressMapFromRequest(): Record<string, LenAddress> {
    const map: Record<string, LenAddress> = {};
    const req = this.state.matchRequest();
    if (!req) return map;
    for (const len of req.legalEntities) {
      if (len.groupId) {
        map[len.groupId] = {
          street1: len.billingAddress?.street1 ?? '',
          street2: len.billingAddress?.street2 ?? '',
          city: len.billingAddress?.city ?? '',
          state: len.billingAddress?.state ?? '',
          zip: len.billingAddress?.zip ?? '',
        };
      }
    }
    return map;
  }

  private buildExtractedLenNamesFromRequest(): Record<string, string> {
    const map: Record<string, string> = {};
    const req = this.state.matchRequest();
    if (!req) return map;
    for (const len of req.legalEntities) {
      if (len.groupId && len.name) {
        map[len.groupId] = len.name;
      }
    }
    return map;
  }

  private buildFastTrackInfo(): void {
    if (this.state.fastTrackMode() !== 'fast') return;

    // Build skipped steps info
    const skipped = this.state.fastTrackSkippedSteps();
    const analysis = this.state.fastTrackAnalysis();
    const steps = this.state.steps();

    const stepLabelMap: Record<string, string> = {};
    for (const s of steps) {
      stepLabelMap[s.key] = s.label;
    }

    const reasonMap: Record<string, string> = {
      'len': analysis?.canSkipLen ? 'All entities matched or new' : '',
      'sa': analysis?.canSkipSa ? 'All addresses matched or new' : '',
      'meter': analysis?.canSkipMeter ? 'All meters matched or new' : '',
      'bill-review': analysis?.allOnUtility
        ? 'All bills are utility-only'
        : analysis?.canSkipBillReview
          ? 'All supplier bills have known plans'
          : '',
    };

    this.fastTrackSkippedSteps = skipped.map(key => ({
      key,
      label: stepLabelMap[key] || key,
      reason: reasonMap[key] || undefined,
    }));

    // Get warnings from store
    this.fastTrackWarningsList = this.state.fastTrackWarnings();
  }

  switchToManualReview(): void {
    this.state.exitFastTrack();
  }

  /** Every bill was skipped as already imported: close the session without calling import. */
  finishWithoutImport(): void {
    this.state.setPreviewBillFile(null);
    this.state.clearPersistedState();
    this.importComplete.emit({
      success: true,
      errors: [],
      legalEntities: [],
      summary: { lenCount: 0, serviceAddressCount: 0, meterCount: 0, billCount: 0, contractCount: 0 },
    });
  }

  // --- Post-Import Navigation ---

  /** Navigate back to where the user entered the flow */
  goToPortfolio(): void {
    // Emit importComplete so the shell/dashboard can clear bills
    if (this.importResponse) {
      this.importComplete.emit(this.importResponse);
    }

    const ctx = this.state.getContext();
    const qp = { queryParams: { selectedSection: 'Portfolio' } };
    if (ctx?.meterId && ctx?.serviceAddressId) {
      this.router.navigate(['/advisor/portfolio/details/customer', ctx.customerId, 'service-address', ctx.serviceAddressId, 'meter', ctx.meterId], qp);
    } else if (ctx?.serviceAddressId) {
      this.router.navigate(['/advisor/portfolio/details/customer', ctx.customerId, 'service-address', ctx.serviceAddressId], qp);
    } else if (ctx?.lenId) {
      this.router.navigate(['/advisor/portfolio/details/customer', ctx.customerId, 'len', ctx.lenId], qp);
    } else if (ctx?.customerId) {
      this.router.navigate(['/advisor/portfolio/details/customer', ctx.customerId], qp);
    } else {
      this.router.navigate(['/advisor/portfolio']);
    }
  }

  /** Navigate to the customer that owns the imported data */
  goToCustomer(): void {
    // Emit importComplete so the shell/dashboard can clear bills
    if (this.importResponse) {
      this.importComplete.emit(this.importResponse);
    }

    const customerId = this.state.selectedCustomerId() || this.state.getContext()?.customerId;
    if (customerId) {
      this.router.navigate(['/advisor/portfolio/details/customer', customerId], { queryParams: { selectedSection: 'Portfolio' } });
    } else {
      this.router.navigate(['/advisor/portfolio']);
    }
  }

  private computePostImportNav(): void {
    const ctx = this.state.getContext();
    if (ctx?.meterId) this.goBackLabel = 'Back to Meter';
    else if (ctx?.serviceAddressId) this.goBackLabel = 'Back to Service Address';
    else if (ctx?.lenId) this.goBackLabel = 'Back to Legal Entity';
    else if (ctx?.customerId) this.goBackLabel = 'Back to Customer';
    else this.goBackLabel = 'Back to Portfolio';

    if (ctx?.level === 'PORTFOLIO') {
      this.showViewCustomer = true;
    } else {
      const selectedId = this.state.selectedCustomerId();
      this.showViewCustomer = !!selectedId && String(selectedId) !== String(ctx?.customerId);
    }
  }
}

interface SummaryBill {
  groupId: string;
  startDate: string;
  endDate: string;
  amount: number;
  usage: number;
  provider: string;
  isDual: boolean;
  fileName: string;
  energyPlanLabel: string | null;
  /** Source documents for a paired (DUAL) bill */
  children?: SummaryBillChild[];
  expanded: boolean;
}
